import { ReactNode } from "react";
import { ChevronDown } from "untitledui-js-base";
import { cx } from "shared/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { BoomCrmPaywall } from "shared/app/BoomCrmPaywall/BoomCrmPaywall";
import { PAYWALL_COPY } from "shared/app/BoomCrmPaywall/constants";
import { AiLeasingAgentVisibility } from "types/landlord/address";
import { StatePill } from "./StatePill";
import { VisibilityPopover } from "./VisibilityPopover";

const LABEL = "AI leasing agent";

// Negative margins keep the pill aligned with the sibling facts while the
// hover / open background still wraps label + pill with a 6×8 inset.
const TRIGGER_CLASS =
  "group -mx-2 -my-1.5 flex flex-col gap-1 rounded-lg px-2 py-1.5 text-left hover:bg-alpha-40 data-[state=open]:bg-alpha-40";

const Label = () => (
  <span className="text-sm font-normal text-content-400">{LABEL}</span>
);

const Value = ({ children }: { children: ReactNode }) => (
  <span className="flex min-h-5 items-center gap-1.5">{children}</span>
);

/**
 * The "AI leasing agent" fact in the unit header (CRM-1620): whether the
 * partner's agent can see this unit. Click opens an explanation popover; the
 * locked (BoomCRM Core) variant opens the paywall card with "Book a demo"
 * instead. Nothing here changes data.
 */
export const AiLeasingAgentField = ({
  visibility,
  propertyName,
  unitListed,
  onGoToListUnit
}: {
  visibility: AiLeasingAgentVisibility;
  propertyName: string;
  unitListed: boolean;
  onGoToListUnit: () => void;
}) => {
  const locked = visibility.state === "locked";

  return (
    <Popover placement="bottom-start" offsetValue={10}>
      <PopoverTrigger asChild>
        <button type="button" className={TRIGGER_CLASS}>
          <Label />
          <Value>
            <StatePill state={visibility.state} />
            <ChevronDown
              size="14"
              aria-hidden
              className="stroke-content-300 transition-transform group-data-[state=open]:rotate-180"
            />
          </Value>
        </button>
      </PopoverTrigger>
      {/* The paywall card is its own 666px surface; the explanation box is 360px. */}
      <PopoverContent
        className={cx(
          "z-30 max-w-[calc(100vw-2rem)]",
          locked ? "w-[666px]" : "w-[360px]"
        )}
      >
        {locked ? (
          <BoomCrmPaywall {...PAYWALL_COPY.aiLeasingAgent} />
        ) : (
          <VisibilityPopover
            visibility={visibility}
            propertyName={propertyName}
            unitListed={unitListed}
            onGoToListUnit={onGoToListUnit}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
